import {
  InventoryPrivateRoomSummary,
  InventorySearchResponse,
} from "@/features/private-room/list/adapters/get-privaterooms/get-privaterooms.types";
import { apiWobaBackend } from "../api-woba-backend";
import { z } from "zod";
import {
  CreatePrivateOfficeParams,
  GetPrivateOfficeUpdateRequestsResponse,
  OptimizeImageRequest,
  OptimizeImageResponse,
  PictureProperty,
  PrivateOfficeApprovalStatus,
  PrivateOfficeUpdateRequest,
  ResultItem,
  SearchPrivateOfficeInventoryParams,
  SearchPrivateOfficesParams,
  SearchParams,
  UpdatePrivateOfficeParams,
} from "./types";
import { apiInventoryRequests } from "../api-inventory-requests";
import { apiWobaInventory } from "../api-woba-inventory";
import { AmenitiesData } from "@/components/amenities-form/amenities-form.types";
import { PRIVATE_OFFICE_STATUS_MAP, PrivateOfficeStatus } from "@/@types";
import { STATUS_PRIVATE_OFFICE } from "@/constants/status-private-office.ts";
import { adaptUpdateHistoryResponse } from "./adapters/update-history-adapter";

export const ALLOWED_CHECK_FIELDS = [
  "status",
  "price",
  "size",
  "capacity",
  "photos",
] as const;

type AllowedCheckField = (typeof ALLOWED_CHECK_FIELDS)[number];

const amenitiesWithAttributesSchema = z.array(
  z.object({
    id: z.number().optional(),
    status: z.string({ required_error: "Obrigatório" }),
    name: z.string(),
    attributes: z
      .array(
        z.object({
          id: z.number().optional(),
          content: z.string().optional(),
          name: z.string(),
          ["content_type"]: z.string(),
        }),
      )
      .optional(),
  }),
);

const groupedAmenitiesSchema = z.array(
  z.object({
    name: z.string(),
    amenities: amenitiesWithAttributesSchema,
  }),
);

type MyObjectOrProperty<T> = T | Partial<T>;
export const privateOfficeService = {
  async getGroupedAmenitiesForm(privateOfficeId?: string | number) {
    const { data } = await apiWobaBackend.get(
      `/private-offices/amenities-form?${privateOfficeId ? `privateOfficeId=${privateOfficeId}` : ""}`,
    );

    return groupedAmenitiesSchema.parse(data.amenities);
  },

  async createPrivateOffice(
    data: CreatePrivateOfficeParams,
  ): Promise<{ private_office: { id: number } }> {
    const response = await apiWobaBackend.post("/private-offices/", {
      ...data,
      guaranteeFee: data.guaranteeFee?.toString(),
    });

    return response.data;
  },

  async updatePrivateOffice({
    data,
    id,
  }: {
    data: MyObjectOrProperty<UpdatePrivateOfficeParams>;
    id: string | number;
  }) {
    await apiWobaBackend.put(`/private-offices/${id}`, {
      ...data,
      guaranteeFee: data.guaranteeFee?.toString(),
    });
  },

  async deletePrivateOffice(id: string | number) {
    await apiWobaBackend.delete(`/private-offices/${id}`);
  },

  async listPrivateOffices({
    spaceId,
    take,
  }: {
    spaceId?: number[] | string[];
    take?: number;
  }): Promise<InventoryPrivateRoomSummary[]> {
    const search = new URLSearchParams();

    spaceId?.forEach((id) => {
      search.append("spaceId[]", id?.toString());
    });

    if (take) {
      search.append("take", take?.toString());
    }

    const response = await apiWobaBackend.get(
      `/private-offices/list?${search.toString()}`,
    );

    return response.data.private_offices;
  },

  async listPrivateOfficesInventory(
    params: SearchPrivateOfficeInventoryParams,
  ): Promise<InventoryPrivateRoomSummary[]> {
    const mapStatusFromInventory = (
      status?: PrivateOfficeStatus,
    ): STATUS_PRIVATE_OFFICE => {
      if (status) {
        return PRIVATE_OFFICE_STATUS_MAP[status];
      }

      return STATUS_PRIVATE_OFFICE.AVAILABLE;
    };

    const response = await apiWobaInventory.post<InventorySearchResponse[]>(
      "/private-offices/search",
      params,
    );

    return response.data.map(
      (item: InventorySearchResponse) =>
        ({
          id: item.id,
          search_id: item.searchId,
          space_id: item.spaceId,
          label: item.label,
          capacity: item.capacity,
          monthly_price: item.monthlyPrice,
          amenities: [],
          extra_services: [],
          notes: item.notes,
          status: mapStatusFromInventory(item.status),
          size: item.size,
          yearly_price: item.monthlyPrice * 12,
          category: 1,
        }) as InventoryPrivateRoomSummary,
    );
  },

  async searchPrivateOffices(
    params: SearchPrivateOfficesParams,
    view?: "full" | "map",
  ) {
    const url = view
      ? `/private-offices/filters?view=${view}`
      : "/private-offices/filters";

    const response = await apiWobaBackend.post(url, {
      ...params,
      useNewSearch: true,
    });
    return response.data;
  },

  async searchByMode(params: SearchParams) {
    // O /search do woba-backend exclui salas de integração (ex.: VIP Office) quando o campo
    // é omitido (woba-backend#1309). O Vision AI mostra essas salas com preço estimado, então
    // o default aqui é incluí-las; quem chama ainda pode mandar `false` explicitamente.
    const response = await apiWobaBackend.post("/private-offices/search", {
      ...params,
      filters: { includeIntegration: true, ...params.filters },
    });
    return response.data;
  },

  async searchPrivateOfficesByIds(
    ids: string[],
    unitCreditPrice: number = 80,
    { includeDeleted = true }: { includeDeleted?: boolean } = {},
  ): Promise<ResultItem[]> {
    if (!ids.length) return [];
    const response = await apiWobaBackend.post("/private-offices/filters", {
      demands: {
        privateOfficeIds: {
          propertyType: ["private-office"],
          privateOfficeStatus: [0, 1],
          unitCreditPrice,
          searchType: "privateOfficeIds",
          privateOfficeIds: ids,
          skipRecentUpdatesFilter: true,
          categoryIds: [1],
          approvalStatus: PrivateOfficeApprovalStatus.approved,
          includeLegacy: true,
          includeDeleted,
        },
      },
      useNewSearch: true,
    });
    return response.data?.results ?? [];
  },

  searchPrivateOfficeById(
    privateOfficeId: string,
    unitCreditPrice: number = 80,
  ): Promise<ResultItem[]> {
    return this.searchPrivateOfficesByIds([privateOfficeId], unitCreditPrice, {
      includeDeleted: false,
    });
  },

  async uploadCsv(file: File): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await apiWobaBackend.post(
      "/private-offices/csv",
      formData,
    );
    return response.data;
  },

  getUpdateHistory: async (privateOfficeId: string) => {
    const response = await apiWobaInventory.get(
      `/private-offices/${privateOfficeId}/history`,
    );
    return adaptUpdateHistoryResponse(response.data);
  },

  getUpdateRequests: async (params?: {
    page?: number;
    limit?: number;
    status?: "pending" | "approved" | "rejected";
    space_name?: string;
  }): Promise<GetPrivateOfficeUpdateRequestsResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append("grouped", "true");

    if (params?.page) {
      searchParams.append("page", params.page.toString());
    }
    if (params?.limit) {
      searchParams.append("limit", params.limit.toString());
    }
    if (params?.status) {
      searchParams.append("status", params.status);
    }
    if (params?.space_name && params.space_name.trim()) {
      searchParams.append("space_name", params.space_name.trim());
    }

    const response = await apiInventoryRequests.get(
      `/private-offices/update-requests?${searchParams.toString()}`,
    );
    return response.data;
  },

  approveUpdateRequest: async (
    requestId: string,
    approvalData: {
      replace_property?: PictureProperty;
      new_image_url?: string;
      userEmail: string;
    },
  ): Promise<PrivateOfficeUpdateRequest> => {
    const response = await apiInventoryRequests.patch(
      `/private-offices/update-requests/${requestId}/approve`,
      approvalData,
    );
    return response.data.data;
  },

  rejectUpdateRequest: async (
    requestId: string,
    reason: string,
  ): Promise<PrivateOfficeUpdateRequest> => {
    const response = await apiInventoryRequests.patch(
      `/private-offices/update-requests/${requestId}/reject`,
      {
        reason,
      },
    );
    return response.data.data;
  },

  sendRequestForAgents: async (payload: {
    privateOfficeId: number | string;
  }): Promise<void> => {
    await apiInventoryRequests.post(`/private-offices/agents`, {
      room_id: payload.privateOfficeId,
    });
  },

  optimizeImage: async (
    payload: OptimizeImageRequest,
  ): Promise<OptimizeImageResponse> => {
    const response = await apiWobaInventory.post<OptimizeImageResponse>(
      "/private-offices/enhance-photo",
      payload,
    );
    return response.data;
  },

  // Function to fetch amenities for cloning (uses apiWobaInventory like edit page)
  async getAmenitiesForCloning(
    privateOfficeId: string | number,
  ): Promise<AmenitiesData | null> {
    try {
      const response = await apiWobaInventory.get(
        `/amenities-form?privateOfficeId=${privateOfficeId}`,
      );

      // Transform the response: API returns array directly, we need {amenities: array} format
      const transformedData: AmenitiesData = {
        amenities: response.data,
      };

      return transformedData;
    } catch (error) {
      console.error("Error fetching amenities for cloning:", error);
      return null;
    }
  },

  touchPrivateOffice: async (
    privateOfficeId: string | number,
    fields?: AllowedCheckField[],
  ): Promise<void> => {
    if (fields && fields.length > 0) {
      const invalidFields = fields.filter(
        (field) => !ALLOWED_CHECK_FIELDS.includes(field as AllowedCheckField),
      );

      if (invalidFields.length > 0) {
        throw new Error(
          `Invalid fields: ${invalidFields.join(", ")}. Allowed fields are: ${ALLOWED_CHECK_FIELDS.join(", ")}`,
        );
      }
    }

    const url = `/private-offices/${privateOfficeId}/touch`;
    const params =
      fields && fields.length > 0 ? { fields: fields.join(",") } : undefined;

    await apiWobaInventory.patch(url, undefined, { params });
  },
};
